import React from "react";

import PageHelmet from "@Components/shared/PageHelmet";
import { DownloadsSkeleton } from "@Components/shared/downloads/DownloadsSkeleton";
import { QueryKeys, ThreeMinutes } from "@Constants";
import LapARTotumLogo from "@Images/LapARTotumLogo";
import TotumMobileLogo from "@Images/TotumMobileLogo";
import TotumNativeLogo from "@Images/TotumNativeLogo";
import DownloadsService from "@Services/DownloadsService";
import { prettifyName } from "@Utils";
import { DownloadOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, Col, Divider, Layout, Row, Typography } from "antd";
const { Title } = Typography;
const { Content } = Layout;

const LOGOS = {
    LapARNative: LapARTotumLogo,
    TotumNative: TotumNativeLogo,
    TotumMobile: TotumMobileLogo,
};

const DownloadsPage: React.FC = () => {
    const { isFetched, data: downloads } = useQuery(
        [QueryKeys.Downloads],
        async () => {
            const service = new DownloadsService();
            const result = await service.getAll();
            if (result.hasErrors) return [];

            const applications = result.value.applications;
            return applications.filter((application) => application.branding === window?.["branding"]);
        },
        {
            staleTime: ThreeMinutes,
            refetchOnWindowFocus: false,
            refetchIntervalInBackground: true,
            placeholderData: [],
        }
    );

    return (
        <article>
            <PageHelmet name="Downloads" />
            <section className="downloads-page">
                <Row gutter={[16, 8]}>
                    {!isFetched
                        ? [...Array(3).keys()].map(() => (
                              <Col xxl={6} md={8} xs={24}>
                                  <DownloadsSkeleton />
                              </Col>
                          ))
                        : null}
                    {downloads?.map((download) => {
                        const releases = download.stages.find((stage) => stage.name === "release");
                        const latestReleaseBuild = releases?.builds?.[0];
                        const platforms = latestReleaseBuild?.platforms?.filter(
                            (p) => !!p.url && ["android", "ios", "windows"].includes(p.name)
                        );
                        const Logo = LOGOS?.[download.name];
                        if (!platforms.length || !Logo) return null;

                        return platforms.map((platform) => {
                            const name = platform.name === "ios" ? "iOS" : prettifyName(platform.name);
                            return (
                                <Col xxl={6} md={8} xs={24}>
                                    <Card className="newUI-subtle white">
                                        <Content>
                                            <Row>
                                                <Col>
                                                    <Logo style={{ float: "right" }} />
                                                </Col>
                                                <Col style={{ paddingLeft: "20px" }}>
                                                    <Row>
                                                        <Title level={3}>{download.title}</Title>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        </Content>
                                        <Title level={5} className="card-stat">
                                            {name}
                                        </Title>

                                        <Row style={{ minHeight: "30px" }}>{releases.description}</Row>
                                        <Divider />
                                        <Button
                                            className="secondary"
                                            href={platform.url}
                                            icon={<DownloadOutlined />}
                                            target={["android", "ios"].includes(platform.name) ? "_blank" : undefined}
                                        >
                                            {"Get " + download.title}
                                        </Button>
                                    </Card>
                                </Col>
                            );
                        });
                    })}
                </Row>
            </section>
        </article>
    );
};

export default DownloadsPage;
